import styled from '@emotion/styled';
import { tokens, Text, Icon } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';
import { SetupLogoIcon } from '../SetupWizard/SetupLogoIcon';
import type { SetupLogoIconProps } from '../SetupWizard/SetupLogoIcon';

const SMALL_SCREEN = '(min-width: 1024px)';
const MEDIUM_SCREEN = '(min-width: 1370px)';
const LARGE_SCREEN = '(min-width: 1440px)';
const ICONS = [
  'microsoft',
  'nintendo',
  'adobe',
  't-mobile',
  'hubspot',
  'invision',
  'panasonic',
  'decathlon',
];

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ContentWrapper = styled.div`
  max-width: 415px;
  padding: 0 ${tokens.spacer.x3};
  box-sizing: content-box;

  @media ${LARGE_SCREEN} {
    padding: 0 64px;
  }
}
`;

const BenefitsTitle = styled(Text)`
  width: 100%;
  font-size: 21px;
  white-space: initial;
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacer.x1};
  margin-bottom: ${tokens.spacer.x3};
  padding: 0;
`;

const ItemText = styled(Text)`
  font-size: 14px;
`;

const LogoList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  justify-items: flex-start;
  align-items: center;
  column-gap: 30px;
  row-gap: 26px;
  max-width: 410px;

  @media ${SMALL_SCREEN} {
    grid-template-columns: repeat(4, 1fr);
  }

  @media ${MEDIUM_SCREEN} {
    justify-items: center;
  }
`;

const BackgroundEllipse = styled.div`
  position: fixed;
  width: 586px;
  height: 470px;
  bottom: 0;
  right: 0;
  transform: translate(50%, 40%);

  background: radial-gradient(
    105% 117.79% at 48.77% 80.34%,
    rgba(161, 143, 214, 0.4) 8.43%,
    rgba(195, 240, 247, 0.4) 58.85%,
    rgba(195, 240, 247, 0) 100%
  );
  filter: blur(50px);
`;

const Item = styled.li`
  display: flex;
  align-items: center;
`;

const StyledIcon = styled(Icon)`
  margin-right: ${tokens.spacer.x1};
`;

const ListItem = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <Item>
    <StyledIcon icon="check_alt" color="text_success" /> {children}
  </Item>
);

export const ValueProposition = (): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Wrapper>
      <ContentWrapper>
        <BenefitsTitle style={{ marginBottom: tokens.spacer.x2 }} textType="header4" as="h2">
          {t('activation:benefits.title')}
        </BenefitsTitle>
        <List>
          <ListItem>
            <ItemText as="span" textColor="text_light">
              {t('activation:benefits.list.code-loaded-async')}
            </ItemText>
          </ListItem>
          <ListItem>
            <ItemText as="span" textColor="text_light">
              {t('activation:benefits.list.GDPR')}
            </ItemText>
          </ListItem>
          <ListItem>
            <ItemText as="span" textColor="text_light">
              {t('activation:benefits.list.number-installed-sites')}
            </ItemText>
          </ListItem>
        </List>
        <LogoList>
          {ICONS.map((icon) => (
            <SetupLogoIcon key={icon} name={icon as SetupLogoIconProps['name']} />
          ))}
        </LogoList>
      </ContentWrapper>
      <BackgroundEllipse />
    </Wrapper>
  );
};
